import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Question } from '@/types/question';
import { PhoneInput } from '../../components/phone-input';
import { useState } from 'react';
import { Loader2, File, X } from 'lucide-react';
import { ApiEndpoint, apiService } from '../../lib/api';

interface DynamicFormProps {
  domain: string;
  question: Question;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

const DynamicForm = ({
  domain,
  question,
  value,
  onChange,
  error,
}: DynamicFormProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setFileName(file.name);
    try {
      const response = await apiService.upload(ApiEndpoint.upload, file, {
        folder: 'Invitee',
        domain: domain,
      });
      if (response.url) {
        onChange(response.url);
      }
    } catch (err: any) {
      error = `Error uploading file: ${err.message}`;
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const clearFile = () => {
    onChange('');
    setFileName(null);
  };

  const renderInput = () => {
    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type={question.type}
              placeholder={question.placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={cn(error && 'border-red-500')}
            />
          </div>
        );

      case 'tel':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <PhoneInput
              value={value}
              onChange={onChange}
              required={question.required}
              modalPopover={false}
              className={cn(error && 'border-red-500')}
            />
          </div>
        );

      case 'number':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type="number"
              placeholder={question.placeholder}
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
              className={cn(error && 'border-red-500')}
            />
          </div>
        );

      case 'textarea':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              placeholder={question.placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={cn(error && 'border-red-500', 'min-h-[80px]')}
            />
          </div>
        );

      case 'dropdown':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <Select value={value} onValueChange={onChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {question.options?.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'radio':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <RadioGroup value={value} onValueChange={onChange}>
              {question.options?.map((option) => (
                <div key={option.key} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.key} id={option.key} />
                  <Label htmlFor={option.key}>{option.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'checkbox':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            {question.options?.map((option) => (
              <div key={option.key} className="flex items-center space-x-2">
                <Checkbox
                  id={option.key}
                  checked={(value || []).includes(option.key)}
                  onCheckedChange={(checked) => {
                    const currentValues = value || [];
                    const newValues = checked
                      ? [...currentValues, option.key]
                      : currentValues.filter((v: any) => v !== option.key);
                    onChange(newValues);
                  }}
                />
                <Label htmlFor={option.key}>{option.label}</Label>
              </div>
            ))}
          </div>
        );

      case 'date':
        return (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-base">
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type={question.type}
              placeholder={question.placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={cn(error && 'border-red-500')}
            />
          </div>
        );

      case 'file':
        return (
          <div className="space-y-2">
            <Label>
              {question.label}{' '}
              {question.required && <span className="text-red-500">*</span>}
            </Label>
            <div className="flex flex-col gap-2">
              {/* File uploaded state */}
              {value && fileName && !isUploading && (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-background/50">
                  <File size={16} className="text-muted-foreground" />
                  <span className="text-sm flex-1 truncate">{fileName}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFile}
                    className="h-6 w-6 p-0"
                  >
                    <X size={16} />
                  </Button>
                </div>
              )}

              {/* Uploading state */}
              {isUploading && (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-background/50">
                  <Loader2
                    size={16}
                    className="animate-spin text-muted-foreground"
                  />
                  <span className="text-sm">Uploading {fileName}...</span>
                </div>
              )}

              {/* Upload button/input */}
              {(!value || isUploading) && (
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    asChild
                    disabled={isUploading}
                    className={cn(error && 'border-red-500')}
                  >
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {isUploading ? 'Uploading...' : 'Choose File'}
                    </label>
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {renderInput()}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default DynamicForm;
